import { HttpHandler, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, debounceTime, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';
import { authTokenInterceptorFilter } from './filter-endpoint.interceptor';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { CurrentUser } from '../models/current-user.model';
import { HttpCancelService } from '../services/http-cancel.service';
import { BehaviorSubject, from, Subject } from 'rxjs';

const jwtInterceptorState = {
  initialized: false,
  refreshing: false,
  refreshTokenSubject: new BehaviorSubject<string | null>(null),
  refreshDebounce$: new Subject<void>()
};

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {

  
  const authService = inject(AuthenticationService);
  const cancelService = inject(HttpCancelService);
  const filterRequest = inject(authTokenInterceptorFilter);

  const user = authService.currentUserValue;

  // Skip if request is filtered, uploading file, or no access token
  if (filterRequest(req) || (req.body instanceof File) || !user?.accessToken) {
    return next(req);
  }
  const reqWithToken = injectToken(req, authService);


  // Refresh-token endpoint should not trigger unauthorized handling
  if (req.url.includes(API_ENDPOINTS.AUTHENTICATION.REFRESH_TOKEN)) {
    return nextWithCancellation(next, reqWithToken, cancelService);
  }
  
  return nextWithCancellation(next, reqWithToken, cancelService).pipe(
    catchError((error) => {
      if (error.status === HttpStatusCode.Unauthorized) {
        return handleUnauthorized(next, req, authService, cancelService);
      }
      throw error;
    })
  );
 
};

const nextWithCancellation = (next: HttpHandlerFn, req: HttpRequest<any>, cancelService: HttpCancelService) =>
  next(req).pipe(
    takeUntil(
      cancelService.cancelPendingRequest$.pipe(
        tap(() => {
          throw new Error('Request is canceled');
        }),
      ),
    ),
  );

const injectToken = (request: HttpRequest<any>,authService: AuthenticationService): HttpRequest<any> => {
  const user = authService.getCurrentUserValue();
  const orgId = authService.getOrgId();
  const orgCd = authService.getOrgCd();
  const mapId = authService.getMapId() ?? "0";
  const companyID = authService.getCompanyId();
  const officeSelected = authService.getOfficeSelected();
  return request.clone({
    setHeaders: {
      authorization: `Bearer ${user?.accessToken}`,
      userId: `${user?.userId}`,
      orgId: `${orgId}`,
      orgCd: `${orgCd}`,
      mapId: `${mapId}`,
      companyId: `${companyID}`,
      officeCd: `${officeSelected?.officecd}`,
      isLoginAuth: `${authService.getIsLoginAuth()}`,
      // corpPrefix: `${sessionInfo.corpPrefix}`,
      // officeCd: `${sessionInfo.officeCd}`,
      // officeVersion: `${sessionInfo.officeVersion}`
    },
  });
}



const handleUnauthorized = (
  next: HttpHandlerFn,
  request: HttpRequest<any>,
  authService: AuthenticationService,
  cancelService: HttpCancelService
) => {

  const user = authService.currentUserValue;

  // CASE 1: refresh is not in progress
  if (!jwtInterceptorState.refreshing) {
    jwtInterceptorState.refreshing = true;
    jwtInterceptorState.refreshTokenSubject.next("");

    return from(authService.getIsLoginAuth() ? 
    authService.refreshTokenAuth(user!.accessToken ?? "", user!.refreshToken ?? "") : 
    authService.refreshToken(user!.refreshToken ?? "")).pipe(
      switchMap((newToken) => {

        if (!newToken) {
          authService.logout();
        }

        jwtInterceptorState.refreshing = false;
        jwtInterceptorState.refreshTokenSubject.next(newToken);

        return nextWithCancellation(
          next,
          injectToken(request, authService),
          cancelService
        ).pipe(
          catchError((error) => {
            if (error.status === HttpStatusCode.Unauthorized) {
              authService.logout();
            }
            throw error;
          })
        );
      }),
      catchError((err) => {
        jwtInterceptorState.refreshing = false;
        authService.logout();
        throw err;
      })
    );
  }

  // CASE 2: refresh is already in progress
  return jwtInterceptorState.refreshTokenSubject.pipe(
    filter((token) => token !== ""),
    take(1),
    switchMap(() =>
      nextWithCancellation(
        next,
        injectToken(request, authService),
        cancelService
      ).pipe(
        catchError((error) => {
          if (error.status === HttpStatusCode.Unauthorized) {
            authService.logout();
          }
          throw error;
        })
      )
    )
  );
};